import { FC } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import type { WorkDateField, WorkRoleField } from "@api/workItem.api";
import { WORK_ROLES } from "@components/organizationView/clients-work/editing/workItemEditing";
import { CommandItem } from "@design-system/CommandItem/CommandItem";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    COMMAND_DIRECTORY_LABELS,
    type ClientsWorkCommandOptions,
    CommandsDirectory,
    CommandsListProps,
} from "./lib";
import { TaskBulkOptionsCommandsList } from "./TaskBulkOptionsCommandsList";
import { useCommands } from "./useCommands";
import styles from "./Commands.module.scss";

const TEAM_DIRECTORIES: Record<WorkRoleField, CommandsDirectory> = {
    preparers: CommandsDirectory.CLIENTS_WORK_BULK_PREPARER,
    reviewers: CommandsDirectory.CLIENTS_WORK_BULK_REVIEWER,
    managers: CommandsDirectory.CLIENTS_WORK_BULK_MANAGER,
};

const TEAM_STEPS = WORK_ROLES.map((role) => ({
    ...role,
    directory: TEAM_DIRECTORIES[role.field],
}));

/** Whether Cmd+K has anyone to offer for `field`; an empty list gets no step. */
const hasMemberOptions = (
    memberOptions: ClientsWorkCommandOptions["memberOptions"] | undefined,
    field: WorkRoleField,
) => (memberOptions?.[field]?.length ?? 0) > 0;

export const ClientsWorkBulkCommandsList: FC<CommandsListProps> = ({
    options = {},
}) => {
    const { open } = useCommands();
    const work = options.clientsWork;
    if (!work) {
        return null;
    }
    const {
        heading,
        memberOptions,
        dateSteps,
        onViewInActions,
        onOpenClient,
        onCancel,
    } = work;
    const hasTeamSteps = TEAM_STEPS.some(({ field }) =>
        hasMemberOptions(memberOptions, field),
    );

    return (
        <Command.Group heading={heading}>
            {hasTeamSteps && (
                <CommandItem
                    keywords={["preparer", "reviewer", "manager", "assign"]}
                    onSelect={() =>
                        open(CommandsDirectory.CLIENTS_WORK_BULK_TEAM, options)
                    }
                >
                    Update engagement team
                </CommandItem>
            )}
            {dateSteps && (
                <>
                    <CommandItem
                        keywords={["date", "start"]}
                        onSelect={() =>
                            open(
                                CommandsDirectory.CLIENTS_WORK_BULK_WORK_START,
                                options,
                            )
                        }
                    >
                        Update work start
                    </CommandItem>
                    <CommandItem
                        keywords={["date", "end", "delivery", "due"]}
                        onSelect={() =>
                            open(
                                CommandsDirectory.CLIENTS_WORK_BULK_WORK_END,
                                options,
                            )
                        }
                    >
                        Update work end
                    </CommandItem>
                </>
            )}
            {onViewInActions && (
                <CommandItem
                    keywords={["actions", "tasks", "close"]}
                    onSelect={onViewInActions}
                >
                    View in Actions
                </CommandItem>
            )}
            {onOpenClient && (
                <CommandItem
                    keywords={["client", "workspace"]}
                    onSelect={onOpenClient}
                >
                    Open client
                </CommandItem>
            )}
            {onCancel && (
                <CommandItem keywords={["cancel", "stop"]} onSelect={onCancel}>
                    Cancel
                </CommandItem>
            )}
            <CommandItem
                keywords={["delete", "remove"]}
                shortcut="delete"
                onSelect={work.onDelete}
            >
                Delete
            </CommandItem>
        </Command.Group>
    );
};

/** Update engagement team: pick the role, then the person. */
export const ClientsWorkTeamCommandsList: FC<CommandsListProps> = ({
    options = {},
}) => {
    const { open } = useCommands();
    const memberOptions = options.clientsWork?.memberOptions;
    return (
        <Command.Group heading="Update engagement team">
            {TEAM_STEPS.filter(({ field }) =>
                hasMemberOptions(memberOptions, field),
            ).map(({ label, directory }) => (
                <CommandItem
                    key={directory}
                    onSelect={() => open(directory, options)}
                >
                    {label}
                </CommandItem>
            ))}
        </Command.Group>
    );
};

/** The people who can take one role on every item. */
export const ClientsWorkMemberCommandsList: FC<
    CommandsListProps & { field: WorkRoleField }
> = ({ options = {}, field }) => (
    <TaskBulkOptionsCommandsList
        heading={COMMAND_DIRECTORY_LABELS[TEAM_DIRECTORIES[field]]}
        options={options.clientsWork?.memberOptions?.[field] ?? []}
    />
);

/** A calendar for one work date; picking a day updates every item. */
export const ClientsWorkDateCommandsList: FC<
    CommandsListProps & { field: WorkDateField }
> = ({ options = {}, field }) => {
    const { close } = useCommands();
    const step = options.clientsWork?.dateSteps?.[field];
    if (!step) {
        return null;
    }
    return (
        // cmdk's root swallows every Enter to select its own item, which would
        // stop a focused calendar day from being pressed from the keyboard.
        <div
            className={styles.dateStep}
            onKeyDown={(event) => {
                if (event.key === "Enter") {
                    event.stopPropagation();
                }
            }}
        >
            <DatePicker
                mode="day"
                variant="embedded"
                required
                value={step.value}
                minDate={step.minDate}
                maxDate={step.maxDate}
                onValueChange={(date) => {
                    if (date) {
                        step.onSelect(date);
                        close();
                    }
                }}
            />
        </div>
    );
};
